(() => {
  const headerLinkSelector = '#header-group a[href]';
  const siteLinkSelector = 'a[href]';
  const mainSelector = '#MainContent';
  const pageCache = new Map();
  const loadedScripts = new Set(
    [...document.scripts]
      .map((script) => script.src)
      .filter(Boolean)
  );

  let navigationId = 0;
  let progressTimer;
  let progressStartedAt = 0;

  const navigationProgress = document.createElement('div');
  navigationProgress.className = 'beambox-navigation-progress';
  navigationProgress.setAttribute('aria-hidden', 'true');
  navigationProgress.innerHTML = '<span></span>';

  const navigationProgressStyle = document.createElement('style');
  navigationProgressStyle.textContent = `
    .beambox-navigation-progress {
      position: fixed;
      z-index: 2147483647;
      inset: 0 0 auto;
      block-size: 3px;
      overflow: hidden;
      pointer-events: none;
      opacity: 0;
      transition: opacity 160ms ease;
    }
    .beambox-navigation-progress > span {
      display: block;
      inline-size: var(--beambox-navigation-progress, 0%);
      block-size: 100%;
      background: #5e59f7;
      box-shadow: 0 0 7px rgb(94 89 247 / 42%);
      transition: inline-size 180ms ease-out;
    }
    .beambox-navigation-progress.is-visible { opacity: 1; }
    @media (max-width: 749px) {
      .beambox-navigation-progress { block-size: 2px; }
    }
  `;

  document.head.append(navigationProgressStyle);
  document.body.append(navigationProgress);

  const setProgress = (value) => {
    navigationProgress.style.setProperty('--beambox-navigation-progress', `${Math.min(100, Math.max(0, value))}%`);
  };

  const startProgress = () => {
    window.clearInterval(progressTimer);
    progressStartedAt = Date.now();
    setProgress(8);
    navigationProgress.classList.add('is-visible');
    let value = 8;
    progressTimer = window.setInterval(() => {
      value = Math.min(78, value + Math.max(1, (78 - value) * 0.12));
      setProgress(value);
    }, 180);
  };

  const finishProgress = () => {
    window.clearInterval(progressTimer);
    setProgress(100);
    const visibleFor = Date.now() - progressStartedAt;
    window.setTimeout(() => {
      navigationProgress.classList.remove('is-visible');
      window.setTimeout(() => setProgress(0), 180);
    }, Math.max(120, 260 - visibleFor));
  };

  const isEligibleLink = (link) => {
    if (!(link instanceof HTMLAnchorElement)) return false;
    if (!link.matches(siteLinkSelector)) return false;
    if (link.hasAttribute('download') || link.target && link.target !== '_self') return false;

    const destination = new URL(link.href, window.location.href);
    if (destination.origin !== window.location.origin) return false;
    if (destination.pathname === window.location.pathname && destination.search === window.location.search) return false;

    return true;
  };

  const cacheKey = (url) => `${url.pathname}${url.search}`;

  const getPage = (url) => {
    const key = cacheKey(url);
    const cached = pageCache.get(key);
    if (cached) return cached;

    const pageRequest = fetch(url.href, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Unable to load ${url.pathname}`);
        return response.text();
      })
      .then((html) => new DOMParser().parseFromString(html, 'text/html'))
      .catch((error) => {
        pageCache.delete(key);
        throw error;
      });

    pageCache.set(key, pageRequest);
    return pageRequest;
  };

  const wait = (promise, timeout = 2500) => Promise.race([
    promise,
    new Promise((resolve) => window.setTimeout(resolve, timeout)),
  ]);

  const ensureStyles = async (documentToLoad, destination) => {
    const existingStyles = new Set(
      [...document.querySelectorAll('link[rel~="stylesheet"][href]')]
        .map((link) => new URL(link.href, window.location.href).href)
    );

    const styleLoads = [...documentToLoad.querySelectorAll('link[rel~="stylesheet"][href]')]
      .map((sourceLink) => ({
        sourceLink,
        href: new URL(sourceLink.getAttribute('href'), destination.href).href,
      }))
      .filter(({ href }) => !existingStyles.has(href))
      .map(({ sourceLink, href }) => new Promise((resolve) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        if (sourceLink.media) link.media = sourceLink.media;
        if (sourceLink.integrity) link.integrity = sourceLink.integrity;
        if (sourceLink.crossOrigin) link.crossOrigin = sourceLink.crossOrigin;
        link.addEventListener('load', resolve, { once: true });
        link.addEventListener('error', resolve, { once: true });
        document.head.append(link);
      }));

    await wait(Promise.all(styleLoads));
  };

  const preloadCriticalImages = (nextMain, destination) => {
    const images = [...nextMain.querySelectorAll('img[fetchpriority="high"], img[loading="eager"]')]
      .slice(0, 2)
      .map((image) => image.getAttribute('src'))
      .filter(Boolean)
      .map((source) => new URL(source, destination.href).href);

    return wait(Promise.all(images.map((source) => new Promise((resolve) => {
      const image = new Image();
      image.onload = resolve;
      image.onerror = resolve;
      image.src = source;
    }))), 1800);
  };

  const syncMetadata = (documentToLoad) => {
    document.title = documentToLoad.title || document.title;

    [
      'meta[name="description"]',
      'link[rel="canonical"]',
      'meta[property="og:title"]',
      'meta[property="og:description"]',
      'meta[name="twitter:title"]',
      'meta[name="twitter:description"]',
    ].forEach((selector) => {
      const current = document.head.querySelector(selector);
      const incoming = documentToLoad.head.querySelector(selector);
      if (incoming) current?.replaceWith(document.importNode(incoming, true));
      else current?.remove();
    });
  };

  const syncBodyClasses = (documentToLoad) => {
    const managedClass = /^(page-width-|card-hover-effect-|polly-navigation-|polly-custom-pointer-)/;
    [...document.body.classList]
      .filter((className) => managedClass.test(className))
      .forEach((className) => document.body.classList.remove(className));
    [...documentToLoad.body.classList]
      .filter((className) => managedClass.test(className))
      .forEach((className) => document.body.classList.add(className));
  };

  const updateCurrentNavigation = (destination) => {
    document.querySelectorAll(headerLinkSelector).forEach((link) => {
      const linkUrl = new URL(link.href, window.location.href);
      const isCurrent = linkUrl.origin === destination.origin
        && linkUrl.pathname === destination.pathname
        && linkUrl.search === destination.search;

      link.classList.toggle('menu-list__link--current', isCurrent);
      link.classList.toggle('menu-list__link--active', isCurrent);
      link.classList.toggle('menu-drawer__menu-item--active', isCurrent);
      if (isCurrent) link.setAttribute('aria-current', 'page');
      else if (link.getAttribute('aria-current') === 'page') link.removeAttribute('aria-current');
    });
  };

  const dispatchSectionEvent = (eventName, scope) => {
    const sections = scope.querySelectorAll?.('[id^="shopify-section-"]') || [];
    sections.forEach((section) => {
      section.dispatchEvent(new CustomEvent(`shopify:section:${eventName}`, { bubbles: true }));
    });
  };

  const collectPageScripts = (nextMain, destination) => [...nextMain.querySelectorAll('script')]
    .map((source) => ({
      source,
      href: source.hasAttribute('src') ? new URL(source.getAttribute('src'), destination.href).href : null,
      code: source.hasAttribute('src') ? null : source.textContent,
    }))
    .filter(({ source }) => !['application/json', 'application/ld+json'].includes(source.type));

  const loadPageScripts = async (sources, isCurrent) => {
    for (const { source, href, code } of sources) {
      if (!isCurrent()) return;
      if (href && loadedScripts.has(href)) continue;

      await wait(new Promise((resolve) => {
        const script = document.createElement('script');
        if (source.type) script.type = source.type;
        if (source.noModule) script.noModule = true;
        if (source.nonce) script.nonce = source.nonce;

        if (href) {
          script.src = href;
          script.async = false;
          if (source.integrity) script.integrity = source.integrity;
          if (source.crossOrigin) script.crossOrigin = source.crossOrigin;
          if (source.referrerPolicy) script.referrerPolicy = source.referrerPolicy;
          script.addEventListener('load', resolve, { once: true });
          script.addEventListener('error', resolve, { once: true });
        } else {
          script.textContent = code;
          window.queueMicrotask(resolve);
        }

        document.body.append(script);
      }));

      if (href) loadedScripts.add(href);
    }
  };

  const closeOpenNavigation = () => {
    document.querySelectorAll('#header-group details[open]').forEach((details) => {
      details.removeAttribute('open');
    });
  };

  const swapPage = async (documentToLoad, destination, { restoreScroll = false, isCurrent } = {}) => {
    const currentMain = document.querySelector(mainSelector);
    const incomingMain = documentToLoad.querySelector(mainSelector);
    if (!currentMain || !incomingMain) throw new Error('The destination page has no main content');
    const pageScripts = collectPageScripts(incomingMain, destination);

    await Promise.all([
      ensureStyles(documentToLoad, destination),
      preloadCriticalImages(incomingMain, destination),
    ]);
    if (!isCurrent()) return false;

    dispatchSectionEvent('unload', currentMain);
    incomingMain
      .querySelectorAll('script:not([type="application/json"]):not([type="application/ld+json"])')
      .forEach((script) => script.remove());
    currentMain.replaceWith(document.importNode(incomingMain, true));

    syncMetadata(documentToLoad);
    syncBodyClasses(documentToLoad);
    updateCurrentNavigation(destination);
    await loadPageScripts(pageScripts, isCurrent);
    if (!isCurrent()) return false;

    const mountedMain = document.querySelector(mainSelector);
    dispatchSectionEvent('load', mountedMain);
    if (restoreScroll && history.state?.softNavigationScroll) {
      window.scrollTo(history.state.softNavigationScroll.x, history.state.softNavigationScroll.y);
    } else if (destination.hash) {
      document.getElementById(destination.hash.slice(1))?.scrollIntoView();
    } else {
      window.scrollTo(0, 0);
    }
    return true;
  };

  const navigate = async (destination, { historyMode = 'push', restoreScroll = false } = {}) => {
    const currentNavigationId = ++navigationId;
    const isCurrent = () => currentNavigationId === navigationId;

    const currentMain = document.querySelector(mainSelector);
    currentMain?.setAttribute('aria-busy', 'true');
    startProgress();

    try {
      if (historyMode === 'push') {
        const currentState = typeof history.state === 'object' && history.state ? history.state : {};
        history.replaceState({
          ...currentState,
          softNavigationScroll: { x: window.scrollX, y: window.scrollY },
        }, '', window.location.href);
      }

      const documentToLoad = await getPage(destination);
      if (!isCurrent()) return;
      setProgress(62);

      const didSwap = await swapPage(documentToLoad, destination, { restoreScroll, isCurrent });
      if (!didSwap || !isCurrent()) return;
      setProgress(90);

      if (historyMode === 'push') {
        history.pushState({ softNavigation: true }, '', destination.href);
      }
    } catch (error) {
      if (isCurrent()) window.location.assign(destination.href);
    } finally {
      if (isCurrent()) {
        document.querySelector(mainSelector)?.removeAttribute('aria-busy');
        finishProgress();
      }
    }
  };

  // Shopify can mark an external menu item as active when it shares the current path.
  // Normalize the initial server-rendered state too, not only after a soft navigation.
  updateCurrentNavigation(new URL(window.location.href));

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!isEligibleLink(link)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    closeOpenNavigation();
    navigate(new URL(link.href, window.location.href));
  }, true);

  document.addEventListener('pointerover', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!isEligibleLink(link) || !link.matches(headerLinkSelector) || link.contains(event.relatedTarget)) return;
    getPage(new URL(link.href, window.location.href)).catch(() => {});
  });

  document.addEventListener('focusin', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (isEligibleLink(link) && link.matches(headerLinkSelector)) {
      getPage(new URL(link.href, window.location.href)).catch(() => {});
    }
  });

  window.addEventListener('popstate', () => {
    navigate(new URL(window.location.href), { historyMode: 'pop', restoreScroll: true });
  });
})();
